<template>
  <UIDialog
    :is-open
    :z-index
    title="Additional instructions"
    size="2md"
    @close="handleCancel"
  >
    <div class="add-account-instructions__content">
      <div v-if="instructions.text">
        {{ instructions.text }}
      </div>
      <ol class="add-account-instructions__steps">
        <li
          v-for="(step, index) in instructions.steps"
          :key="index"
          v-html="DOMPurify.sanitize(step)"
        />
      </ol>
      <UICheckbox
        v-if="instructions.confirmationText"
        v-model="agreement"
        :label="instructions.confirmationText"
      />
    </div>
    <template #footer>
      <UIButton
        label="Continue"
        full
        :disabled="!!instructions.confirmationText && !agreement"
        @click="handleContinue"
      />
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DOMPurify from 'dompurify'

import type { ModalClass } from '@types'
import type { ConnectorInstructions } from '../../utils/types'

import { UIButton } from '@ui/buttons'
import { UICheckbox } from '@ui/controllers'
import { UIDialog } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
  instructions: ConnectorInstructions
}

type Emits = {
  cancel: []
  continue: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const agreement = ref(false)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const handleClose = () => {
  props.modal.close()
  agreement.value = false
}

const handleCancel = () => {
  handleClose()
  emit('cancel')
}

const handleContinue = () => {
  if (props.instructions.confirmationText && !agreement.value) return
  handleClose()
  emit('continue')
}
</script>

<style scoped>
.add-account-instructions {
  &__content {
    @apply space-y-4;
  }

  &__steps {
    @apply pl-6;
    @apply list-decimal;
    @apply space-y-1;

    :deep(a) {
      @apply text-indigo-500 dark:text-indigo-400;
      @apply underline hover:no-underline;
    }
  }
}
</style>
